import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Loader } from 'lucide-react';
interface Message {
  id: string;
  type: 'bot' | 'user';
  content: string;
  options?: string[];
}
interface ChatbotSurveyProps {
  onClose: () => void;
}
export function ChatbotSurvey({
  onClose
}: ChatbotSurveyProps) {
  const [messages, setMessages] = useState<Message[]>([{
    id: '1',
    type: 'bot',
    content: "Hi! I'm here to help you join our research study. Which best describes you?",
    options: ['Solo Founder', 'Cofounder', 'Coach/Advisor', 'Investor/Accelerator']
  }]);
  const [isTyping, setIsTyping] = useState(false);
  const [userRole, setUserRole] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  const addMessage = (content: string, type: 'bot' | 'user', options?: string[]) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      type,
      content,
      options
    };
    setMessages(prev => [...prev, newMessage]);
  };
  const simulateTyping = async (callback: () => void, delay = 1000) => {
    setIsTyping(true);
    await new Promise(resolve => setTimeout(resolve, delay));
    setIsTyping(false);
    callback();
  };
  const handleRoleSelection = (role: string) => {
    setUserRole(role);
    addMessage(role, 'user');
    simulateTyping(() => {
      const roleMessages: Record<string, {
        message: string;
        url: string;
      }> = {
        'Solo Founder': {
          message: "Perfect! I'm redirecting you to the Solo Founder survey. It takes about 15 minutes and covers your unique experience building alone.",
          url: '/solo-survey'
        },
        Cofounder: {
          message: "Excellent! I'm redirecting you to the Cofounder survey. It takes about 25 minutes and explores your partnership dynamics in depth.",
          url: '/cofounder-survey'
        },
        'Coach/Advisor': {
          message: "Great! I'm redirecting you to the Coach & Advisor survey. It takes about 20 minutes and captures your insights from working with multiple partnerships.",
          url: '/mentor-survey'
        },
        'Investor/Accelerator': {
          message: "Perfect! I'm redirecting you to the Investor survey. It takes about 20 minutes and focuses on how you evaluate founding teams.",
          url: '/investor-survey'
        }
      };
      const roleData = roleMessages[role];
      if (roleData) {
        addMessage(roleData.message, 'bot');
        // Redirect after 2 seconds
        setTimeout(() => {
          window.location.href = roleData.url;
        }, 2000);
      }
    }, 1000);
  };
  return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-39">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} className="bg-white rounded-[16px] w-full max-w-2xl h-[600px] flex flex-col overflow-hidden" style={{
      boxShadow: '0 20px 60px rgba(26, 61, 58, 0.2)'
    }} data-id="element-40">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1A3D3A] to-[#0D2926] p-6 flex justify-between items-center" data-id="element-41">
          <div data-id="element-42">
            <h2 className="text-white mb-1" style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: '24px',
            fontWeight: 500
          }} data-id="element-43">
              Join Research Study
            </h2>
            <p className="text-white/70 text-sm" style={{
            fontFamily: "'DM Sans', sans-serif"
          }} data-id="element-44">
              Let's find the right survey for you
            </p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors" data-id="element-45">
            <X className="w-6 h-6" data-id="element-46" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-[#F8FAF9]" data-id="element-47">
          {messages.map(message => <motion.div key={message.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-48">
              <div className={`max-w-[80%] rounded-lg p-4 ${message.type === 'user' ? 'bg-[#1A3D3A] text-white' : 'bg-white border-2 border-[#1A3D3A]/10'}`} style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '15px',
            lineHeight: 1.6
          }} data-id="element-49">
                {message.content}

                {message.options && <div className="mt-4 space-y-2" data-id="element-50">
                    {message.options.map((option, index) => <button key={index} onClick={() => handleRoleSelection(option)} className="w-full px-4 py-3 bg-[#C5F467] text-[#1A3D3A] rounded-lg hover:bg-[#B5EAD7] transition-all text-left font-medium" style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '14px'
              }} data-id="element-51">
                        {option}
                      </button>)}
                  </div>}
              </div>
            </motion.div>)}

          {isTyping && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="flex justify-start" data-id="element-52">
              <div className="bg-white border-2 border-[#1A3D3A]/10 rounded-lg p-4 flex items-center gap-2" data-id="element-53">
                <Loader className="w-4 h-4 animate-spin text-[#1A3D3A]" data-id="element-54" />
                <span className="text-[#3D5654]" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '14px'
            }} data-id="element-55">
                  Typing...
                </span>
              </div>
            </motion.div>}

          <div ref={messagesEndRef} data-id="element-56" />
        </div>
      </motion.div>
    </div>;
}